import React, { useContext, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../../App';
import StoryCard from '../common/StoryCard';
import { stories } from '../sections/SuccessStories';

const translations = {
  en: {
    title: 'Our Success Stories',
    subtitle: 'Read inspiring stories of transformation and hope',
    filterAll: 'All Categories',
    searchPlaceholder: 'Search stories...',
    noResults: 'No stories found matching your search.',
    categories: {
      all: 'All',
      education: 'Education',
      'women-empowerment': 'Women Empowerment',
      'community-development': 'Community Development'
    }
  },
  hi: {
    title: 'हमारी सफलता की कहानियां',
    subtitle: 'परिवर्तन और आशा की प्रेरक कहानियां पढ़ें',
    filterAll: 'सभी श्रेणियां',
    searchPlaceholder: 'कहानियां खोजें...',
    noResults: 'आपकी खोज से मेल खाती कोई कहानी नहीं मिली।',
    categories: {
      all: 'सभी',
      education: 'शिक्षा',
      'women-empowerment': 'महिला सशक्तिकरण',
      'community-development': 'समुदाय विकास'
    }
  }
};

// Category mapping between English and Hindi
const categoryMapping: { [key: string]: string } = {
  'Education': 'शिक्षा',
  'Women Empowerment': 'महिला सशक्तिकरण',
  'Community Development': 'समुदाय विकास',
  'शिक्षा': 'Education',
  'महिला सशक्तिकरण': 'Women Empowerment',
  'समुदाय विकास': 'Community Development'
};

const Stories: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Filter stories based on search query and category
  const filteredStories = useMemo(() => {
    let filtered = stories[language];

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(story => {
        // Get the category in current language
        const storyCategory = story.category;
        
        // Get the category name in English (for key matching)
        const englishCategory = language === 'en' 
          ? storyCategory 
          : categoryMapping[storyCategory];

        // Convert to key format (lowercase, hyphenated)
        const categoryKey = englishCategory.toLowerCase().replace(/\s+/g, '-');
        return categoryKey === selectedCategory;
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(story =>
        story.name.toLowerCase().includes(query) ||
        story.story.toLowerCase().includes(query) ||
        story.fullStory.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [language, searchQuery, selectedCategory]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative bg-gradient-to-r from-primary/10 to-secondary/10 py-20"
      >
        {/* Hero Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="/images/blog-hero.jpg" // Make sure to add your image in the public/images folder
            alt="Blog Hero"
            className="w-full h-full object-cover object-center opacity-20"
            style={{ height: '400px' }} // Fixed height for the hero image
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-baloo font-bold text-gray-800 mb-4"
            >
              {t.title}
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-600 font-hindi"
            >
              {t.subtitle}
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* Stories Grid Section */}
      <section className="py-16">
        <div className="container-custom">
          {/* Search and Filter */}
          <div className="mb-12 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="w-full md:w-96">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              {Object.entries(t.categories).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-full font-hindi transition-colors ${
                    key === selectedCategory
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {/* Stories Grid */}
          {filteredStories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredStories.map((story, index) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <StoryCard {...story} delay={0} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 font-hindi text-lg">{t.noResults}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Stories; 